const PROMOTION_STORAGE_KEY = 'global_promotions';

// Helper function to check if a promotion applies to a product
const doesPromotionApplyToProduct = (promotion, productId, productCategory) => {
  const productIdStr = String(productId);
  const promoProductIds = (promotion.productIds || []).map((pid) => String(pid));
  
  // Check if productId is directly in the promotion's productIds
  if (promoProductIds.includes(productIdStr)) {
    return true;
  }
  
  // Check if promotion applies to the product's category
  // IMPORTANT: Only check category if promotion has a direct categories field
  // If promotion has specific productIds, it should ONLY apply to those products, not all products in the same category
  if (productCategory && promotion.categories && Array.isArray(promotion.categories) && promotion.categories.length > 0) {
    const productCategoryLower = String(productCategory).toLowerCase();
    const promotionCategories = promotion.categories.map(cat => String(cat).toLowerCase());
    if (promotionCategories.includes(productCategoryLower)) {
      return true;
    }
  }
  
  // If promotion doesn't have categories field, it should only apply to specific productIds
  // Do NOT infer category-based matching from productIds
  return false;
};

export const getActivePromotionsForProduct = (productId, productCategory = null) => {
  try {
    const stored = localStorage.getItem(PROMOTION_STORAGE_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored);
    const promotions = Array.isArray(parsed) ? parsed : [];
    
    const now = new Date();
    const productIdStr = String(productId);
    
    return promotions.filter((promotion) => {
      // Skip if promotion is null or undefined (deleted)
      if (!promotion) {
        return false;
      }
      
      // Check if promotion has been deleted (no id, _id, or promotionId)
      if (!promotion.id && !promotion._id && !promotion.promotionId) {
        return false;
      }
      
      // Check if promotion applies to this product (by productId or category)
      // The doesPromotionApplyToProduct function now only checks:
      // 1. Direct productId match, OR
      // 2. Category match if promotion has explicit categories field
      // It does NOT infer category matching from productIds
      if (!doesPromotionApplyToProduct(promotion, productId, productCategory)) {
        return false;
      }

      // Check if manually set to inactive
      if (promotion.status === 'inactive') {
        return false;
      }

      // Check start datetime
      if (promotion.startDateTime) {
        const startDate = new Date(promotion.startDateTime);
        if (now < startDate) {
          return false;
        }
      } else if (promotion.startDate) {
        const startDate = new Date(promotion.startDate + 'T00:00:00');
        if (now < startDate) {
          return false;
        }
      }

      // Check end datetime
      if (promotion.endDateTime) {
        const endDate = new Date(promotion.endDateTime);
        if (now > endDate) {
          return false;
        }
      } else if (promotion.endDate) {
        const endDate = new Date(promotion.endDate + 'T23:59:59');
        if (now > endDate) {
          return false;
        }
      }

      return true;
    });
  } catch (error) {
    console.error('Failed to get promotions for product:', error);
    return [];
  }
};

// New function to get promotions that apply to a product by checking both productIds and categories
// This function requires access to all products to check categories
export const getActivePromotionsForProductWithCategory = (productId, productCategory, allProducts = []) => {
  try {
    const stored = localStorage.getItem(PROMOTION_STORAGE_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored);
    const promotions = Array.isArray(parsed) ? parsed : [];
    
    const now = new Date();
    const productIdStr = String(productId);
    const productCategoryLower = productCategory ? String(productCategory).toLowerCase() : null;
    
    return promotions.filter((promotion) => {
      // Skip if promotion is null or undefined (deleted)
      if (!promotion) {
        return false;
      }
      
      // Check if promotion has been deleted (no id, _id, or promotionId)
      if (!promotion.id && !promotion._id && !promotion.promotionId) {
        return false;
      }
      
      // Check if promotion applies to this product by productId
      const promoProductIds = (promotion.productIds || []).map((pid) => String(pid));
      let appliesByProductId = promoProductIds.includes(productIdStr);
      
      // Check if promotion applies by category
      // IMPORTANT: Only check category if promotion has a direct categories field
      // If promotion has specific productIds, it should ONLY apply to those products, not all products in the same category
      let appliesByCategory = false;
      
      // Check if promotion has a direct categories field (category-based promotion)
      if (productCategoryLower && promotion.categories && Array.isArray(promotion.categories) && promotion.categories.length > 0) {
        const promotionCategories = promotion.categories.map(cat => String(cat).toLowerCase());
        appliesByCategory = promotionCategories.includes(productCategoryLower);
      }
      
      // If promotion has specific productIds, it should ONLY apply to those products
      // Do NOT infer category-based matching from productIds
      // Promotion applies if:
      // 1. Product ID matches directly, OR
      // 2. Promotion has explicit categories field and product category matches
      if (!appliesByProductId && !appliesByCategory) {
        return false;
      }

      // Check if manually set to inactive
      if (promotion.status === 'inactive') {
        return false;
      }

      // Check start datetime
      if (promotion.startDateTime) {
        const startDate = new Date(promotion.startDateTime);
        if (now < startDate) {
          return false;
        }
      } else if (promotion.startDate) {
        const startDate = new Date(promotion.startDate + 'T00:00:00');
        if (now < startDate) {
          return false;
        }
      }

      // Check end datetime
      if (promotion.endDateTime) {
        const endDate = new Date(promotion.endDateTime);
        if (now > endDate) {
          return false;
        }
      } else if (promotion.endDate) {
        const endDate = new Date(promotion.endDate + 'T23:59:59');
        if (now > endDate) {
          return false;
        }
      }

      return true;
    });
  } catch (error) {
    console.error('Failed to get promotions for product:', error);
    return [];
  }
};

export const getBestPromotionForProduct = (productId, productCategory = null, allProducts = []) => {
  // Try with category and allProducts first for better matching
  const activePromotions = allProducts.length > 0 && productCategory
    ? getActivePromotionsForProductWithCategory(productId, productCategory, allProducts)
    : getActivePromotionsForProduct(productId, productCategory);
    
  if (activePromotions.length === 0) {
    return null;
  }

  // Return the promotion with the highest discount value
  return activePromotions.reduce((best, current) => {
    const currentValue = current.discountType === 'amount' 
      ? (current.discountAmount || 0)
      : (current.discount || 0);
    const bestValue = best.discountType === 'amount'
      ? (best.discountAmount || 0)
      : (best.discount || 0);
    
    // For percentage, higher is better. For amount, higher is better too
    return currentValue > bestValue ? current : best;
  });
};

